/**
 * 2D constant-velocity Kalman filter for smoothing noisy ball detections.
 *
 * State vector (4x1): [x, y, vx, vy]
 * Measurement vector (2x1): [x, y]
 *
 * The filter assumes constant velocity between steps with white-noise
 * acceleration; process noise is tuned via `processNoise` (larger = trusts
 * measurements more, smaller = trusts the motion model more). Measurement
 * noise covariance is isotropic scalar `measurementNoise`.
 *
 * We keep matrix math hand-written (no linear-algebra dep) — the matrices
 * are tiny (4x4 at worst) and allocating typed arrays per step would be
 * worse than the current few multiplies. Every operation is pure and the
 * filter mutates only its own private fields.
 */

export interface KalmanState {
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
}

export interface Kalman2DOptions {
  /**
   * Time step between predictions in seconds. Defaults to 1/60 (60 FPS).
   * Can be overridden per-`predict()` call for variable-rate sources.
   */
  readonly dt?: number;
  /** Process-noise variance (per axis). Higher → filter reacts faster. */
  readonly processNoise?: number;
  /** Measurement-noise variance (per axis). Higher → measurements trusted less. */
  readonly measurementNoise?: number;
  /** Optional initial state. Defaults to zeros. */
  readonly initialState?: KalmanState;
  /** Optional initial covariance scale (diagonal). Defaults to a "unknown" large value. */
  readonly initialCovariance?: number;
}

export class Kalman2D {
  // State vector [x, y, vx, vy]
  private sx = 0;
  private sy = 0;
  private svx = 0;
  private svy = 0;

  // 4x4 covariance matrix, row-major.
  private p: number[];

  private readonly q: number;
  private readonly r: number;
  private readonly defaultDt: number;

  constructor(opts: Kalman2DOptions = {}) {
    this.defaultDt = opts.dt ?? 1 / 60;
    this.q = opts.processNoise ?? 1e-3;
    this.r = opts.measurementNoise ?? 1e-2;

    const s = opts.initialState;
    if (s) {
      this.sx = s.x;
      this.sy = s.y;
      this.svx = s.vx;
      this.svy = s.vy;
    }

    const c = opts.initialCovariance ?? 1;
    // Diagonal covariance: positions + velocities.
    this.p = [
      c, 0, 0, 0,
      0, c, 0, 0,
      0, 0, c, 0,
      0, 0, 0, c,
    ];
  }

  /** Returns a snapshot of the current filtered state. */
  getState(): KalmanState {
    return { x: this.sx, y: this.sy, vx: this.svx, vy: this.svy };
  }

  /**
   * Predict one step ahead. Advances the state by `dt` seconds using a
   * constant-velocity model and inflates the covariance by `Q`.
   */
  predict(dt: number = this.defaultDt): KalmanState {
    // x' = F x, with F = [[1,0,dt,0],[0,1,0,dt],[0,0,1,0],[0,0,0,1]]
    this.sx = this.sx + this.svx * dt;
    this.sy = this.sy + this.svy * dt;
    // Velocity is unchanged in the constant-velocity model.

    // P' = F P F^T + Q
    // F P: 4x4 * 4x4. Only rows 0 and 1 change (row i = row i + dt*row i+2).
    const p = this.p;
    const fp = new Array<number>(16);
    for (let c = 0; c < 4; c++) {
      fp[0 * 4 + c] = (p[0 * 4 + c] ?? 0) + dt * (p[2 * 4 + c] ?? 0);
      fp[1 * 4 + c] = (p[1 * 4 + c] ?? 0) + dt * (p[3 * 4 + c] ?? 0);
      fp[2 * 4 + c] = p[2 * 4 + c] ?? 0;
      fp[3 * 4 + c] = p[3 * 4 + c] ?? 0;
    }
    // (F P) F^T: only cols 0 and 1 are affected by the transpose similarly.
    const fpft = new Array<number>(16);
    for (let r = 0; r < 4; r++) {
      fpft[r * 4 + 0] = (fp[r * 4 + 0] ?? 0) + dt * (fp[r * 4 + 2] ?? 0);
      fpft[r * 4 + 1] = (fp[r * 4 + 1] ?? 0) + dt * (fp[r * 4 + 3] ?? 0);
      fpft[r * 4 + 2] = fp[r * 4 + 2] ?? 0;
      fpft[r * 4 + 3] = fp[r * 4 + 3] ?? 0;
    }

    // Q — process noise. Simple diagonal with higher variance on velocities
    // (acceleration-ish) so the filter can still adapt to rapid direction
    // changes (tennis ball trajectory).
    const q = this.q;
    fpft[0] = (fpft[0] ?? 0) + q;
    fpft[5] = (fpft[5] ?? 0) + q;
    fpft[10] = (fpft[10] ?? 0) + q * 10;
    fpft[15] = (fpft[15] ?? 0) + q * 10;

    this.p = fpft;
    return this.getState();
  }

  /**
   * Incorporate a noisy position measurement (x, y). Call after `predict()`.
   * Returns the updated state.
   */
  update(x: number, y: number): KalmanState {
    // H = [[1,0,0,0],[0,1,0,0]] — observe only position.
    // Innovation: y - Hx = (mx - sx, my - sy)
    const innX = x - this.sx;
    const innY = y - this.sy;

    const p = this.p;
    // S = H P H^T + R → 2x2 block = top-left 2x2 of P plus rI
    const s00 = (p[0] ?? 0) + this.r;
    const s01 = p[1] ?? 0;
    const s10 = p[4] ?? 0;
    const s11 = (p[5] ?? 0) + this.r;

    const det = s00 * s11 - s01 * s10;
    if (Math.abs(det) < 1e-12) {
      // Degenerate; skip update but keep state.
      return this.getState();
    }
    const invDet = 1 / det;
    const si00 = s11 * invDet;
    const si01 = -s01 * invDet;
    const si10 = -s10 * invDet;
    const si11 = s00 * invDet;

    // K = P H^T S^{-1} — first two columns of P times S^{-1} (2x2).
    const k = new Array<number>(8); // 4x2
    for (let r = 0; r < 4; r++) {
      const pr0 = p[r * 4 + 0] ?? 0;
      const pr1 = p[r * 4 + 1] ?? 0;
      k[r * 2 + 0] = pr0 * si00 + pr1 * si10;
      k[r * 2 + 1] = pr0 * si01 + pr1 * si11;
    }

    // State update: x = x + K * innovation
    this.sx = this.sx + (k[0] ?? 0) * innX + (k[1] ?? 0) * innY;
    this.sy = this.sy + (k[2] ?? 0) * innX + (k[3] ?? 0) * innY;
    this.svx = this.svx + (k[4] ?? 0) * innX + (k[5] ?? 0) * innY;
    this.svy = this.svy + (k[6] ?? 0) * innX + (k[7] ?? 0) * innY;

    // Covariance update: P = (I - K H) P
    // (I - K H) has columns 0,1 set to (-K), other columns identity-ish.
    // Simpler: compute KH (4x4) where only the top-left two columns matter.
    const kh = new Array<number>(16).fill(0);
    for (let r = 0; r < 4; r++) {
      kh[r * 4 + 0] = k[r * 2 + 0] ?? 0;
      kh[r * 4 + 1] = k[r * 2 + 1] ?? 0;
    }
    const iMinusKH = new Array<number>(16);
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        const identity = r === c ? 1 : 0;
        iMinusKH[r * 4 + c] = identity - (kh[r * 4 + c] ?? 0);
      }
    }
    // P = iMinusKH * P
    const newP = new Array<number>(16).fill(0);
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        let acc = 0;
        for (let i = 0; i < 4; i++) {
          acc += (iMinusKH[r * 4 + i] ?? 0) * (p[i * 4 + c] ?? 0);
        }
        newP[r * 4 + c] = acc;
      }
    }
    this.p = newP;

    return this.getState();
  }

  /**
   * Reset to a known state (e.g. after a detection gap too long to
   * reasonably extrapolate). Covariance returns to the provided scale.
   */
  reset(state: KalmanState, initialCovariance = 1): void {
    this.sx = state.x;
    this.sy = state.y;
    this.svx = state.vx;
    this.svy = state.vy;
    const c = initialCovariance;
    this.p = [
      c, 0, 0, 0,
      0, c, 0, 0,
      0, 0, c, 0,
      0, 0, 0, c,
    ];
  }
}
